<template>
  <div>
    <div class="min-h-screen flex-1 bg-gray-100 px-3 items-center">
      <div class="bg-white w-full rounded-lg shadow overflow-hidden">
        <!-- Header -->
        <div class="h-20 py-3 flex items-center border-gray-200 p-6">
          <div class="flex">
            <div class="text-2xl font-bold text-green-700">{{ $t('partners') || 'Partenaires COD' }}</div>
          </div>
          <div class="w-full flex justify-end">
            <button
              @click="showConvertModal = true"
              class="flex rounded focus:outline-none outline-none text-white px-3 w-1/7 py-2 bg-blue-600 hover:shadow-inner hover:bg-blue-700 transition-all duration-300"
            >
              <i class="material-icons">swap_horiz</i> {{ $t('convert_seller') || 'Convertir Seller' }}
            </button>
            <router-link
              class="flex rounded focus:outline-none outline-none text-white px-3 w-1/7 py-2 bg-green-600 hover:shadow-inner ml-4 hover:bg-green-600 transition-all duration-300"
              to="/partners/new"
            >
              <i class="material-icons">add</i> {{ $t('add_partner') || 'Ajouter Partenaire' }}
            </router-link>
          </div>
        </div>

        <!-- Filters -->
        <div class="px-6 py-4 bg-gray-50 border-b">
          <div class="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div>
              <input
                type="text"
                v-model="filters.search"
                @input="debouncedSearch"
                :placeholder="$t('search') || 'Rechercher...'"
                class="w-full px-3 py-2 border rounded focus:outline-none focus:border-green-500"
              />
            </div>
            <div>
              <select
                v-model="filters.status"
                @change="fetchPartners"
                class="w-full px-3 py-2 border rounded focus:outline-none focus:border-green-500"
              >
                <option value="">{{ $t('all_status') || 'Tous les statuts' }}</option>
                <option value="active">Actif</option>
                <option value="inactive">Inactif</option>
                <option value="blocked">Bloqué</option>
              </select>
            </div>
            <div>
              <select
                v-model="filters.contractType"
                @change="fetchPartners"
                class="w-full px-3 py-2 border rounded focus:outline-none focus:border-green-500"
              >
                <option value="">{{ $t('all_contracts') || 'Tous les contrats' }}</option>
                <option value="standard">Standard</option>
                <option value="premium">Premium</option>
                <option value="enterprise">Enterprise</option>
              </select>
            </div>
            <div>
              <button
                @click="resetFilters"
                class="w-full px-3 py-2 border rounded bg-gray-200 hover:bg-gray-300 transition-colors"
              >
                {{ $t('reset') || 'Réinitialiser' }}
              </button>
            </div>
          </div>
        </div>

        <!-- Loading -->
        <loading :active.sync="loading" :is-full-page="false"></loading>

        <!-- Table -->
        <div class="overflow-x-auto">
          <table class="min-w-full divide-y divide-gray-200">
            <thead class="bg-gray-50">
              <tr>
                <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {{ $t('company') || 'Entreprise' }}
                </th>
                <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {{ $t('contact') || 'Contact' }}
                </th>
                <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {{ $t('contract') || 'Contrat' }}
                </th>
                <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {{ $t('api_stats') || 'Statistiques API' }}
                </th>
                <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {{ $t('status') || 'Statut' }}
                </th>
                <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {{ $t('origin') || 'Origine' }}
                </th>
                <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {{ $t('actions') || 'Actions' }}
                </th>
              </tr>
            </thead>
            <tbody class="bg-white divide-y divide-gray-200">
              <tr v-for="partner in partners" :key="partner._id" class="hover:bg-gray-50">
                <td class="px-6 py-4 whitespace-nowrap">
                  <div class="flex items-center">
                    <div class="flex-shrink-0 h-10 w-10 bg-green-100 rounded-full flex items-center justify-center">
                      <span class="text-green-600 font-bold">{{ getInitials(getCompanyName(partner)) }}</span>
                    </div>
                    <div class="ml-4">
                      <div class="text-sm font-medium text-gray-900">{{ getCompanyName(partner) }}</div>
                      <div class="text-sm text-gray-500">{{ partner.company }}</div>
                    </div>
                  </div>
                </td>
                <td class="px-6 py-4 whitespace-nowrap">
                  <div class="text-sm text-gray-900">{{ partner.email }}</div>
                  <div class="text-sm text-gray-500">{{ partner.phone }}</div>
                </td>
                <td class="px-6 py-4 whitespace-nowrap">
                  <span :class="getContractBadgeClass(getContractType(partner))" class="px-2 inline-flex text-xs leading-5 font-semibold rounded-full">
                    {{ getContractType(partner) }}
                  </span>
                  <div class="text-xs text-gray-500 mt-1">
                    {{ formatDate(getContractStartDate(partner)) }}
                  </div>
                </td>
                <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                  <div>Requêtes: {{ getApiStats(partner, 'totalRequests') }}</div>
                  <div>Commandes: {{ getApiStats(partner, 'totalOrders') }}</div>
                </td>
                <td class="px-6 py-4 whitespace-nowrap">
                  <span :class="getStatusBadgeClass(partner.status)" class="px-2 inline-flex text-xs leading-5 font-semibold rounded-full">
                    {{ partner.status }}
                  </span>
                </td>
                <td class="px-6 py-4 whitespace-nowrap">
                  <div v-if="isConvertedSeller(partner)" class="flex items-center">
                    <span class="px-2 py-1 inline-flex items-center text-xs leading-5 font-semibold rounded-full bg-blue-100 text-blue-800">
                      <i class="material-icons text-xs mr-1">swap_horiz</i>
                      {{ $t('converted') || 'Converti' }}
                    </span>
                    <div class="text-xs text-gray-400 ml-2" :title="$t('converted_at') || 'Converti le'">
                      {{ formatDate(getConvertedAt(partner)) }}
                    </div>
                  </div>
                  <div v-else class="flex items-center">
                    <span class="px-2 py-1 inline-flex items-center text-xs leading-5 font-semibold rounded-full bg-green-100 text-green-800">
                      <i class="material-icons text-xs mr-1">add</i>
                      {{ $t('created') || 'Créé' }}
                    </span>
                  </div>
                </td>
                <td class="px-6 py-4 whitespace-nowrap text-sm font-medium">
                  <div class="flex space-x-2">
                    <router-link
                      :to="'/partners/edit/' + partner._id"
                      class="text-green-600 hover:text-green-900"
                    >
                      <i class="material-icons text-sm">edit</i>
                    </router-link>
                    <router-link
                      :to="'/partners/view/' + partner._id"
                      class="text-blue-600 hover:text-blue-900"
                    >
                      <i class="material-icons text-sm">visibility</i>
                    </router-link>
                    <button
                      @click="showApiCredentials(partner)"
                      class="text-purple-600 hover:text-purple-900"
                      title="Voir les clés API"
                    >
                      <i class="material-icons text-sm">vpn_key</i>
                    </button>
                    <button
                      v-if="isConvertedSeller(partner)"
                      @click="revertToSeller(partner)"
                      class="text-orange-600 hover:text-orange-900"
                      :title="$t('revert_to_seller') || 'Revenir en Seller'"
                    >
                      <i class="material-icons text-sm">undo</i>
                    </button>
                    <button
                      @click="deletePartner(partner)"
                      class="text-red-600 hover:text-red-900"
                      title="Supprimer le partenaire"
                    >
                      <i class="material-icons text-sm">delete</i>
                    </button>
                  </div>
                </td>
              </tr>
              <tr v-if="partners.length === 0 && !loading">
                <td colspan="7" class="px-6 py-12 text-center text-gray-500">
                  {{ $t('no_partners_found') || 'Aucun partenaire trouvé' }}
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <!-- Pagination -->
        <div class="px-6 py-4 flex items-center justify-between border-t border-gray-200">
          <div class="text-sm text-gray-700">
            {{ $t('showing') || 'Affichage de' }} {{ (paginate.currentpage - 1) * paginate.perpage + 1 }} à {{ Math.min(paginate.currentpage * paginate.perpage, paginate.total) }} sur {{ paginate.total }} {{ $t('results') || 'résultats' }}
          </div>
          <div class="flex space-x-2" v-if="paginate.lastpage > 1">
            <button
              @click="changePage(paginate.currentpage - 1)"
              :disabled="paginate.currentpage === 1"
              class="px-3 py-1 border rounded disabled:opacity-50 hover:bg-gray-100"
            >
              <i class="material-icons text-sm">chevron_left</i>
            </button>
            <span class="px-3 py-1">{{ paginate.currentpage }} / {{ paginate.lastpage }}</span>
            <button
              @click="changePage(paginate.currentpage + 1)"
              :disabled="paginate.currentpage === paginate.lastpage"
              class="px-3 py-1 border rounded disabled:opacity-50 hover:bg-gray-100"
            >
              <i class="material-icons text-sm">chevron_right</i>
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- API Credentials Modal -->
    <div v-if="showCredentialsModal" class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div class="bg-white rounded-lg p-6 max-w-lg w-full mx-4">
        <div class="flex justify-between items-center mb-4">
          <h3 class="text-lg font-bold text-gray-900">{{ $t('api_credentials') || 'Clés API' }}</h3>
          <button @click="showCredentialsModal = false" class="text-gray-500 hover:text-gray-700">
            <i class="material-icons">close</i>
          </button>
        </div>
        <div v-if="selectedPartner" class="space-y-4">
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1">API Key</label>
            <div class="flex">
              <input
                type="text"
                :value="getApiKey(selectedPartner)"
                readonly
                class="flex-1 px-3 py-2 border rounded-l bg-gray-50"
              />
              <button
                @click="copyToClipboard(getApiKey(selectedPartner))"
                class="px-3 py-2 border border-l-0 rounded-r bg-gray-100 hover:bg-gray-200"
              >
                <i class="material-icons text-sm">content_copy</i>
              </button>
            </div>
          </div>
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1">API Secret</label>
            <div class="flex">
              <input
                type="password"
                :value="getApiSecret(selectedPartner)"
                readonly
                class="flex-1 px-3 py-2 border rounded-l bg-gray-50"
              />
              <button
                @click="copyToClipboard(getApiSecret(selectedPartner))"
                class="px-3 py-2 border border-l-0 rounded-r bg-gray-100 hover:bg-gray-200"
              >
                <i class="material-icons text-sm">content_copy</i>
              </button>
            </div>
          </div>
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1">API Ref</label>
            <div class="flex">
              <input
                type="text"
                :value="getApiRef(selectedPartner)"
                readonly
                class="flex-1 px-3 py-2 border rounded-l bg-gray-50"
              />
              <button
                @click="copyToClipboard(getApiRef(selectedPartner))"
                class="px-3 py-2 border border-l-0 rounded-r bg-gray-100 hover:bg-gray-200"
              >
                <i class="material-icons text-sm">content_copy</i>
              </button>
            </div>
          </div>
          <div class="pt-4 border-t">
            <button
              @click="regenerateKeys"
              class="w-full px-4 py-2 bg-orange-500 text-white rounded hover:bg-orange-600 transition-colors"
            >
              <i class="material-icons text-sm mr-1">refresh</i>
              {{ $t('regenerate_keys') || 'Régénérer les clés' }}
            </button>
            <p class="text-xs text-gray-500 mt-2 text-center">
              Attention: la régénération invalidera les anciennes clés
            </p>
          </div>
        </div>
      </div>
    </div>

    <!-- Convert Seller Modal -->
    <div v-if="showConvertModal" class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div class="bg-white rounded-lg p-6 max-w-2xl w-full mx-4 max-h-screen overflow-y-auto">
        <div class="flex justify-between items-center mb-4">
          <h3 class="text-lg font-bold text-gray-900">{{ $t('convert_seller_to_partner') || 'Convertir un Seller en Partenaire' }}</h3>
          <button @click="closeConvertModal" class="text-gray-500 hover:text-gray-700">
            <i class="material-icons">close</i>
          </button>
        </div>

        <!-- Step 1: Search and Select Seller -->
        <div v-if="convertStep === 1" class="space-y-4">
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-2">{{ $t('search_seller') || 'Rechercher un Seller' }}</label>
            <input
              type="text"
              v-model="sellerSearch"
              @input="debouncedSellerSearch"
              :placeholder="$t('search_by_name_email') || 'Rechercher par nom, email, entreprise...'"
              class="w-full px-3 py-2 border rounded focus:outline-none focus:border-blue-500"
            />
          </div>

          <div v-if="loadingSellers" class="text-center py-4">
            <i class="material-icons animate-spin">autorenew</i>
          </div>

          <div v-else-if="sellers.length > 0" class="border rounded max-h-64 overflow-y-auto">
            <div
              v-for="seller in sellers"
              :key="seller._id"
              @click="selectSeller(seller)"
              :class="['p-3 border-b cursor-pointer hover:bg-gray-50 transition-colors', selectedSeller && selectedSeller._id === seller._id ? 'bg-blue-50 border-blue-200' : '']"
            >
              <div class="flex justify-between items-center">
                <div>
                  <div class="font-medium text-gray-900">{{ seller.fullName }}</div>
                  <div class="text-sm text-gray-500">{{ seller.email }}</div>
                  <div class="text-xs text-gray-400">{{ seller.company }} | {{ seller.phone }}</div>
                </div>
                <div v-if="selectedSeller && selectedSeller._id === seller._id" class="text-blue-600">
                  <i class="material-icons">check_circle</i>
                </div>
              </div>
            </div>
          </div>

          <div v-else-if="sellerSearch && !loadingSellers" class="text-center py-4 text-gray-500">
            {{ $t('no_sellers_found') || 'Aucun seller trouvé' }}
          </div>

          <div class="flex justify-end pt-4 border-t">
            <button
              @click="closeConvertModal"
              class="px-4 py-2 border rounded text-gray-700 hover:bg-gray-100 mr-2"
            >
              {{ $t('cancel') || 'Annuler' }}
            </button>
            <button
              @click="convertStep = 2"
              :disabled="!selectedSeller"
              class="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50"
            >
              {{ $t('next') || 'Suivant' }}
            </button>
          </div>
        </div>

        <!-- Step 2: Configure Partner Details -->
        <div v-if="convertStep === 2" class="space-y-4">
          <div class="bg-blue-50 p-3 rounded mb-4">
            <div class="font-medium text-blue-800">{{ $t('converting') || 'Conversion de:' }} {{ selectedSeller.fullName }}</div>
            <div class="text-sm text-blue-600">{{ selectedSeller.email }}</div>
          </div>

          <div class="grid grid-cols-2 gap-4">
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-1">{{ $t('company_name') || 'Nom de l\'entreprise' }}</label>
              <input
                type="text"
                v-model="convertData.companyName"
                class="w-full px-3 py-2 border rounded focus:outline-none focus:border-blue-500"
              />
            </div>
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-1">{{ $t('contract_type') || 'Type de contrat' }}</label>
              <select
                v-model="convertData.contractType"
                class="w-full px-3 py-2 border rounded focus:outline-none focus:border-blue-500"
              >
                <option value="standard">Standard</option>
                <option value="premium">Premium</option>
                <option value="enterprise">Enterprise</option>
              </select>
            </div>
          </div>

          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1">{{ $t('webhook_url') || 'Webhook URL (optionnel)' }}</label>
            <input
              type="url"
              v-model="convertData.webhookUrl"
              placeholder="https://api.partner.com/webhooks"
              class="w-full px-3 py-2 border rounded focus:outline-none focus:border-blue-500"
            />
          </div>

          <div class="flex justify-between pt-4 border-t">
            <button
              @click="convertStep = 1"
              class="px-4 py-2 border rounded text-gray-700 hover:bg-gray-100"
            >
              {{ $t('back') || 'Retour' }}
            </button>
            <div>
              <button
                @click="closeConvertModal"
                class="px-4 py-2 border rounded text-gray-700 hover:bg-gray-100 mr-2"
              >
                {{ $t('cancel') || 'Annuler' }}
              </button>
              <button
                @click="convertSellerToPartner"
                :disabled="converting"
                class="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 disabled:opacity-50"
              >
                <span v-if="converting">{{ $t('converting') || 'Conversion...' }}</span>
                <span v-else>{{ $t('convert') || 'Convertir en Partenaire' }}</span>
              </button>
            </div>
          </div>
        </div>

        <!-- Step 3: Success - Show Credentials -->
        <div v-if="convertStep === 3" class="space-y-4">
          <div class="text-center mb-4">
            <div class="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <i class="material-icons text-green-600 text-3xl">check</i>
            </div>
            <h3 class="text-lg font-bold text-gray-900">{{ $t('conversion_success') || 'Conversion réussie!' }}</h3>
            <p class="text-gray-600">{{ selectedSeller.fullName }} {{ $t('is_now_partner') || 'est maintenant un Partenaire' }}</p>
          </div>

          <div v-if="convertedCredentials" class="bg-gray-50 p-4 rounded space-y-3">
            <p class="text-sm text-red-600 font-medium text-center">
              {{ $t('save_credentials_warning') || 'Sauvegardez ces informations, elles ne seront plus affichées!' }}
            </p>
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-1">API Key</label>
              <div class="flex">
                <code class="flex-1 bg-white p-2 rounded-l border text-xs break-all">{{ convertedCredentials.apiKey }}</code>
                <button @click="copyToClipboard(convertedCredentials.apiKey)" class="px-3 border border-l-0 rounded-r bg-gray-100 hover:bg-gray-200">
                  <i class="material-icons text-sm">content_copy</i>
                </button>
              </div>
            </div>
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-1">API Secret</label>
              <div class="flex">
                <code class="flex-1 bg-white p-2 rounded-l border text-xs break-all">{{ convertedCredentials.apiSecret }}</code>
                <button @click="copyToClipboard(convertedCredentials.apiSecret)" class="px-3 border border-l-0 rounded-r bg-gray-100 hover:bg-gray-200">
                  <i class="material-icons text-sm">content_copy</i>
                </button>
              </div>
            </div>
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-1">Webhook Secret</label>
              <div class="flex">
                <code class="flex-1 bg-white p-2 rounded-l border text-xs break-all">{{ convertedCredentials.webhookSecret }}</code>
                <button @click="copyToClipboard(convertedCredentials.webhookSecret)" class="px-3 border border-l-0 rounded-r bg-gray-100 hover:bg-gray-200">
                  <i class="material-icons text-sm">content_copy</i>
                </button>
              </div>
            </div>
          </div>

          <div class="flex justify-center pt-4 border-t">
            <button
              @click="finishConversion"
              class="px-6 py-2 bg-green-600 text-white rounded hover:bg-green-700"
            >
              {{ $t('done') || 'Terminé' }}
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  props: {
    currentUser: { type: Object },
    idWharhouse: { type: Object }
  },
  data() {
    return {
      partners: [],
      loading: false,
      filters: {
        search: '',
        status: '',
        contractType: ''
      },
      paginate: {
        total: 0,
        currentpage: 1,
        lastpage: 1,
        perpage: 10
      },
      showCredentialsModal: false,
      selectedPartner: null,
      searchTimeout: null,
      // Convert Seller Modal
      showConvertModal: false,
      convertStep: 1,
      sellerSearch: '',
      sellers: [],
      loadingSellers: false,
      selectedSeller: null,
      converting: false,
      convertedCredentials: null,
      convertData: {
        companyName: '',
        contractType: 'standard',
        webhookUrl: ''
      },
      sellerSearchTimeout: null
    };
  },
  async mounted() {
    await this.fetchPartners();
  },
  methods: {
    async fetchPartners() {
      this.loading = true;
      try {
        const params = {
          page: this.paginate.currentpage,
          limit: this.paginate.perpage,
          ...this.filters
        };

        const response = await this.$server.search('partners', params);
        if (response && response.content) {
          this.partners = response.content.results || [];
          this.paginate.total = response.content.total || 0;
          this.paginate.lastpage = response.content.last_page || 1;
        }
      } catch (error) {
        console.error('Error fetching partners:', error);
        this.$toast.error(this.$t('error_fetching_partners') || 'Erreur lors du chargement des partenaires');
      } finally {
        this.loading = false;
      }
    },
    debouncedSearch() {
      clearTimeout(this.searchTimeout);
      this.searchTimeout = setTimeout(() => {
        this.paginate.currentpage = 1;
        this.fetchPartners();
      }, 300);
    },
    resetFilters() {
      this.filters = {
        search: '',
        status: '',
        contractType: ''
      };
      this.paginate.currentpage = 1;
      this.fetchPartners();
    },
    changePage(page) {
      if (page >= 1 && page <= this.paginate.lastpage) {
        this.paginate.currentpage = page;
        this.fetchPartners();
      }
    },
    showApiCredentials(partner) {
      this.selectedPartner = partner;
      this.showCredentialsModal = true;
    },
    async regenerateKeys() {
      if (!confirm(this.$t('confirm_regenerate_keys') || 'Êtes-vous sûr de vouloir régénérer les clés API ?')) {
        return;
      }
      try {
        this.$toast.info(this.$t('feature_coming_soon') || 'Fonctionnalité à venir');
      } catch (error) {
        console.error('Error regenerating keys:', error);
        this.$toast.error(this.$t('error_regenerating_keys') || 'Erreur lors de la régénération des clés');
      }
    },
    copyToClipboard(text) {
      if (!text) return;
      navigator.clipboard.writeText(text).then(() => {
        this.$toast.success(this.$t('copied_to_clipboard') || 'Copié dans le presse-papiers');
      }).catch(() => {
        this.$toast.error(this.$t('error_copying') || 'Erreur lors de la copie');
      });
    },
    getInitials(name) {
      if (!name) return '?';
      return name.split(' ').map(function(n) { return n[0]; }).join('').toUpperCase().slice(0, 2);
    },
    getCompanyName(partner) {
      if (partner && partner.partnerConfig && partner.partnerConfig.companyName) {
        return partner.partnerConfig.companyName;
      }
      return partner.fullName || '';
    },
    getContractType(partner) {
      if (partner && partner.partnerConfig && partner.partnerConfig.contractType) {
        return partner.partnerConfig.contractType;
      }
      return 'standard';
    },
    getContractStartDate(partner) {
      if (partner && partner.partnerConfig && partner.partnerConfig.contractStartDate) {
        return partner.partnerConfig.contractStartDate;
      }
      return null;
    },
    getApiStats(partner, field) {
      if (partner && partner.partnerConfig && partner.partnerConfig.apiStats && partner.partnerConfig.apiStats[field]) {
        return partner.partnerConfig.apiStats[field];
      }
      return 0;
    },
    getApiKey(partner) {
      if (partner && partner.api && partner.api.key) {
        return partner.api.key;
      }
      return 'Non généré';
    },
    getApiSecret(partner) {
      if (partner && partner.api && partner.api.secret) {
        return partner.api.secret;
      }
      return 'Non généré';
    },
    getApiRef(partner) {
      if (partner && partner.api && partner.api.ref) {
        return partner.api.ref;
      }
      return 'Non généré';
    },
    getContractBadgeClass(type) {
      var classes = {
        standard: 'bg-gray-100 text-gray-800',
        premium: 'bg-blue-100 text-blue-800',
        enterprise: 'bg-purple-100 text-purple-800'
      };
      return classes[type] || classes.standard;
    },
    getStatusBadgeClass(status) {
      var classes = {
        active: 'bg-green-100 text-green-800',
        inactive: 'bg-yellow-100 text-yellow-800',
        blocked: 'bg-red-100 text-red-800'
      };
      return classes[status] || 'bg-gray-100 text-gray-800';
    },
    isConvertedSeller(partner) {
      return partner && partner.partnerConfig && partner.partnerConfig.convertedFromSeller === true;
    },
    getConvertedAt(partner) {
      if (partner && partner.partnerConfig && partner.partnerConfig.convertedAt) {
        return partner.partnerConfig.convertedAt;
      }
      return null;
    },
    formatDate(date) {
      if (!date) return '-';
      return new Date(date).toLocaleDateString('fr-FR');
    },
    async deletePartner(partner) {
      var companyName = this.getCompanyName(partner);
      if (!confirm((this.$t('confirm_delete_partner') || 'Êtes-vous sûr de vouloir supprimer le partenaire') + ' "' + companyName + '" ?')) {
        return;
      }
      this.loading = true;
      try {
        var response = await this.$server.delete('partners', { partnerId: partner._id });
        if (response && !response.error) {
          this.$toast.success(this.$t('partner_deleted') || 'Partenaire supprimé avec succès');
          await this.fetchPartners();
        } else {
          this.$toast.error(response.error || this.$t('error_deleting_partner') || 'Erreur lors de la suppression du partenaire');
        }
      } catch (error) {
        console.error('Error deleting partner:', error);
        this.$toast.error(this.$t('error_deleting_partner') || 'Erreur lors de la suppression du partenaire');
      } finally {
        this.loading = false;
      }
    },
    // Convert Seller Methods
    debouncedSellerSearch() {
      clearTimeout(this.sellerSearchTimeout);
      this.sellerSearchTimeout = setTimeout(() => {
        this.searchSellers();
      }, 300);
    },
    async searchSellers() {
      if (!this.sellerSearch || this.sellerSearch.length < 2) {
        this.sellers = [];
        return;
      }
      this.loadingSellers = true;
      try {
        var response = await this.$server.search('partners', {
          action: 'getSellers',
          search: this.sellerSearch,
          limit: 20
        });
        if (response && response.content) {
          this.sellers = response.content.results || [];
        }
      } catch (error) {
        console.error('Error searching sellers:', error);
        this.sellers = [];
      } finally {
        this.loadingSellers = false;
      }
    },
    selectSeller(seller) {
      this.selectedSeller = seller;
      this.convertData.companyName = seller.company || seller.fullName || '';
    },
    closeConvertModal() {
      this.showConvertModal = false;
      this.convertStep = 1;
      this.sellerSearch = '';
      this.sellers = [];
      this.selectedSeller = null;
      this.converting = false;
      this.convertedCredentials = null;
      this.convertData = {
        companyName: '',
        contractType: 'standard',
        webhookUrl: ''
      };
    },
    async convertSellerToPartner() {
      if (!this.selectedSeller) return;

      this.converting = true;
      try {
        var response = await this.$server.create('partners', {
          action: 'convertSeller',
          sellerId: this.selectedSeller._id,
          companyName: this.convertData.companyName,
          contractType: this.convertData.contractType,
          webhookUrl: this.convertData.webhookUrl
        });

        if (response && response.content && response.content.success) {
          this.convertedCredentials = response.content.credentials;
          this.convertStep = 3;
          this.$toast.success(this.$t('seller_converted') || 'Seller converti en Partenaire avec succès');
        } else {
          this.$toast.error(response.content || this.$t('error_converting_seller') || 'Erreur lors de la conversion');
        }
      } catch (error) {
        console.error('Error converting seller:', error);
        this.$toast.error(error.message || this.$t('error_converting_seller') || 'Erreur lors de la conversion');
      } finally {
        this.converting = false;
      }
    },
    finishConversion() {
      this.closeConvertModal();
      this.fetchPartners();
    },
    async revertToSeller(partner) {
      var companyName = this.getCompanyName(partner);
      if (!confirm((this.$t('confirm_revert_to_seller') || 'Êtes-vous sûr de vouloir reconvertir ce partenaire en Seller?') + '\n\n"' + companyName + '"\n\n' + (this.$t('revert_warning') || 'Cette action supprimera la configuration Partner (webhooks, rate limits, etc.)'))) {
        return;
      }
      this.loading = true;
      try {
        var response = await this.$server.create('partners', {
          action: 'revertToSeller',
          partnerId: partner._id
        });
        if (response && response.content && response.content.success) {
          this.$toast.success(this.$t('partner_reverted') || 'Partenaire reconverti en Seller avec succès');
          await this.fetchPartners();
        } else {
          this.$toast.error(response.content || this.$t('error_reverting_partner') || 'Erreur lors de la reconversion');
        }
      } catch (error) {
        console.error('Error reverting partner:', error);
        this.$toast.error(error.message || this.$t('error_reverting_partner') || 'Erreur lors de la reconversion');
      } finally {
        this.loading = false;
      }
    }
  }
};
</script>
